/**
 * Экран кампаний: район сверху, хозяйства ниже, контуры — по ссылке.
 *
 * Второй вход в ту же модель данных, что и «Что от меня ждут». Числа обязаны
 * сойтись: и агрегат района, и строка хозяйства считаются из одних контурных
 * обязательств, поэтому сумма по хозяйствам не может разойтись с итогом
 * кампании.
 *
 * Три знаменателя показываются вместе и всегда. Один процент не заменяет
 * другой: «половина контуров» и «половина хозяйств» означают разный объём
 * работы, а гектары — третий, ещё раз другой.
 */

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { api } from "../../api/client";
import type {
  CampaignDetail,
  CampaignList as CampaignListResponse,
  CampaignRow,
} from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { layout, PageHeader } from "../../components/AppShell";
import { Disclosure } from "../../components/Disclosure";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import {
  AreaProgress,
  AxisLegend,
  DoneSplit,
  ProgressRow,
  progressStyles,
} from "../../components/Progress";
import { StackedShare } from "../../components/StackedShare";
import { Timeline, type TimelineRow } from "../../components/Timeline";
import {
  Badge,
  Button,
  Card,
  Count,
  DataStatePanel,
  EvidenceBadge,
  InlineAlert,
  MetricCard,
  RequestErrorPanel,
  ScopeNotice,
} from "../../components/ui";
import { denominatorsOf, windowNote } from "../../lib/progress";
import { formatCount, formatDate } from "../../lib/format";
import { COMMON } from "../../i18n/common";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { DENSITY, preview } from "../../lib/levels";
import { campaignStatusLabel, obligationStateLabel } from "../../lib/workVocab";
import features from "../features.module.css";
import { queueAnchor } from "../verification/verificationModel";
import { deadlineView, stateView } from "../works/worksModel";
import styles from "./campaigns.module.css";

const CAMPAIGN_PARAM = "campaign_id";

const m = defineMessages({
  // В чём считает кампания: единица работы — работа на поле (контуре). Без числа.
  nounWork: { id: "canonCampaigns.noun.work", defaultMessage: "{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}", description: "Существительное счёта под полосой «без отчёта: N …»: работа на поле; без числа" },
  // шапка
  title: { id: "canonCampaigns.page.title", defaultMessage: "Ход кампаний", description: "Заголовок экрана «Ход кампаний» (хоким, менеджер) и последняя ступень цепочки уровней" },
  purpose: { id: "canonCampaigns.page.purpose", defaultMessage: "Прогресс считается снизу вверх: работа на поле → контур → хозяйство → район. Три знаменателя показываются вместе, оси «отчитались» и «проверено» — раздельно.", description: "Пояснение под заголовком экрана «Ход кампаний»" },
  loadFailed: { id: "canonCampaigns.state.loadFailed", defaultMessage: "Кампании не загрузились", description: "Заголовок панели ошибки списка кампаний" },
  emptyTitle: { id: "canonCampaigns.state.emptyTitle", defaultMessage: "Кампаний нет", description: "Заголовок пустого состояния списка кампаний" },
  emptyText: { id: "canonCampaigns.state.emptyText", defaultMessage: "В выбранном срезе кампаний не найдено. Синтетические кампании видны только при включённых демо-данных — сервер их не присылает, и экран не изобретает их сам.", description: "Пояснение к пустому списку кампаний" },
  allTitle: { id: "canonCampaigns.all.title", defaultMessage: "Все кампании среза", description: "Заголовок раскрывающегося блока с таблицей всех кампаний" },
  allSummary: {
    id: "canonCampaigns.all.summary",
    defaultMessage: "{count, plural, one {# кампания} few {# кампании} many {# кампаний} other {# кампании}}: работа, состояние, окно, ход",
    description: "Краткое содержание свёрнутого блока со всеми кампаниями; # — число кампаний",
  },
  allMore: { id: "canonCampaigns.all.more", defaultMessage: "развернуть таблицу", description: "Приписка-ссылка свёрнутого блока: раскрыть таблицу кампаний" },
  allCaption: { id: "canonCampaigns.all.caption", defaultMessage: "Кампании района. Числа в строке — те же, что раскроются в детализации: они считаются из одних работ на полях. Ход кампании — одна ячейка с двумя осями и знаменателем: «проверено» не выводится из «отчитались», а его ноль означает «проверяющий ещё не доехал», а не «работа не подтвердилась». Остаток назван отсутствием отчёта, а не невыполнением.", description: "Подпись (caption) таблицы всех кампаний: как читать ячейку хода" },
  colCampaign: { id: "canonCampaigns.all.colCampaign", defaultMessage: "Кампания", description: "Заголовок колонки таблицы кампаний" },
  colWork: { id: "canonCampaigns.all.colWork", defaultMessage: "Работа", description: "Заголовок колонки таблицы кампаний: какую работу ставит кампания" },
  colState: { id: "canonCampaigns.all.colState", defaultMessage: "Состояние", description: "Заголовок колонки таблицы кампаний: запланирована / идёт / завершена…" },
  colWindow: { id: "canonCampaigns.all.colWindow", defaultMessage: "Окно", description: "Заголовок колонки таблицы кампаний: даты окна работ" },
  colProgress: { id: "canonCampaigns.all.colProgress", defaultMessage: "Ход по работам на полях", description: "Заголовок колонки таблицы кампаний: ячейка хода с двумя осями" },
  colFarms: { id: "canonCampaigns.all.colFarms", defaultMessage: "Хозяйств", description: "Заголовок числовой колонки таблицы кампаний: число хозяйств; узкая колонка" },
  // первый взгляд
  headCount: { id: "canonCampaigns.head.count", defaultMessage: "Кампаний в срезе", description: "Подпись KPI: число кампаний в выбранном срезе" },
  headCountBottom: { id: "canonCampaigns.head.countBottom", defaultMessage: "кампания — это работа, поставленная району на окно", description: "Нижняя строка KPI «Кампаний в срезе»: что такое кампания" },
  headRunning: { id: "canonCampaigns.head.running", defaultMessage: "Идут сейчас", description: "Подпись KPI: сколько кампаний сейчас идёт" },
  headRunningBottom: { id: "canonCampaigns.head.runningBottom", defaultMessage: "состояние кампании со стороны сервера", description: "Нижняя строка KPI «Идут сейчас»" },
  headRunningExplain: { id: "canonCampaigns.head.runningExplain", defaultMessage: "«Идёт» — это состояние кампании, которое присылает сервер, а не сравнение дат окна с датой среза: горизонт решает одно место, и второе однажды разошлось бы с ним молча.", description: "Всплывающее пояснение (знак вопроса) у KPI «Идут сейчас»" },
  headProgress: { id: "canonCampaigns.head.progress", defaultMessage: "Ход по работам на полях", description: "Подпись KPI и предмет пояснения: ход всех кампаний среза по работам на полях" },
  headProgressBottom: { id: "canonCampaigns.head.progressBottom", defaultMessage: "сумма по кампаниям среза: работа на поле принадлежит одной кампании", description: "Нижняя строка KPI хода: суммировать по кампаниям можно, двойного счёта нет" },
  headProgressExplain: { id: "canonCampaigns.head.progressExplain", defaultMessage: "Знаменатель — работы на полях кампаний ЭТОГО среза. Хозяйства и гектары здесь не суммируются: одно хозяйство работает в нескольких кампаниях, и сумма по строкам посчитала бы его дважды.", description: "Всплывающее пояснение (знак вопроса) у KPI хода: что в знаменателе" },
  // идущие
  noRunning: { id: "canonCampaigns.open.none", defaultMessage: "Идущих кампаний в срезе нет. Это не «работы нет»: кампании могут быть объявлены, приостановлены или уже закрыты — состояние каждой стоит в таблице ниже.", description: "Пометка, когда ни одна кампания не идёт" },
  runningNote: {
    id: "canonCampaigns.open.note",
    defaultMessage: "Идут сейчас: {shown} из {total}{hasHidden, select, yes {; остальные {hidden} — в таблице ниже} other {}}. Порядок — тот, что отдал сервер: «первая» здесь не значит «главная».",
    description: "Пометка над карточками идущих кампаний: сколько показано из скольких; {hasHidden}=yes, если часть скрыта, тогда {hidden} — сколько",
  },
  cardNote: { id: "canonCampaigns.open.cardNote", defaultMessage: "{work} · окно {from} — {to}", description: "Строка в карточке идущей кампании: название работы и окно; {work} — название работы, {from}/{to} — даты" },
  progressBtn: { id: "canonCampaigns.open.progressBtn", defaultMessage: "Прогресс", description: "Кнопка в карточке кампании: раскрыть детальный прогресс; коротко" },
  verifyLink: { id: "canonCampaigns.open.verifyLink", defaultMessage: "Проверить", description: "Ссылка в карточке кампании: перейти к очереди проверки этой кампании; коротко" },
  // лента окон
  windowsTitle: { id: "canonCampaigns.windows.title", defaultMessage: "Окна кампаний в сезоне", description: "Заголовок ленты окон кампаний на оси времени" },
  windowsDenominator: {
    id: "canonCampaigns.windows.denominator",
    defaultMessage: "{count, plural, one {# кампания} few {# кампании} many {# кампаний} other {# кампании}} среза",
    description: "Знаменатель ленты окон (что показано): число кампаний среза; # — число",
  },
  windowsNoAsOf: { id: "canonCampaigns.windows.noAsOf", defaultMessage: "срез не зафиксирован: линию даты рисовать не по чему", description: "Пометка под лентой окон, когда у среза нет даты и вертикальной линии не будет" },
  windowsRowNote: { id: "canonCampaigns.windows.rowNote", defaultMessage: "{work} · {reported} из {total} работ на полях с отчётом", description: "Оговорка к строке ленты окон: название работы и число работ на полях с отчётом; {reported} и {total} — отформатированные числа" },
  windowsCaption: { id: "canonCampaigns.windows.caption", defaultMessage: "Полоса — ОКНО работы, а не её объём: длина полосы ничего не говорит о числе работ на полях. Вертикальная линия — дата среза, на которую посчитан экран, а не сегодняшний день. Правее последнего известного окна лента не продолжается: прогноза здесь нет.", description: "Подпись под лентой окон кампаний: как читать полосу и вертикальную линию" },
  windowsHidden: { id: "canonCampaigns.windows.hidden", defaultMessage: "Показаны {shown} из {total} датированных окон по началу окна; остальные — в таблице ниже.", description: "Добавка к подписи ленты окон, когда часть окон скрыта; {shown} и {total} — числа" },
  windowsUndated: {
    id: "canonCampaigns.windows.undated",
    defaultMessage: "Ещё {count, plural, one {# кампания} few {# кампании} many {# кампаний} other {# кампании}} без полного окна: у них известен не каждый край, и полосой они не рисуются.",
    description: "Добавка к подписи ленты окон: кампании без полного окна; # — число",
  },
  demo: { id: "canonCampaigns.demo", defaultMessage: "ДЕМО", description: "Бейдж рядом с названием кампании: кампания синтетическая; заглавными, коротко" },
  unknownState: { id: "canonCampaigns.row.unknownState", defaultMessage: "код состояния не из словаря приложения", description: "Подсказка к бейджу с сырым кодом состояния кампании, который интерфейс не знает" },
  shown: { id: "canonCampaigns.row.shown", defaultMessage: "Показано", description: "Кнопка в строке таблицы кампаний: прогресс этой кампании уже раскрыт ниже; коротко" },
  // прогресс кампании
  progressFailed: { id: "canonCampaigns.progress.failed", defaultMessage: "Прогресс не загрузился", description: "Заголовок панели ошибки детального прогресса кампании" },
  progressLoading: { id: "canonCampaigns.progress.loading", defaultMessage: "Загрузка прогресса", description: "Заголовок состояния загрузки детального прогресса кампании" },
  workHint: { id: "canonCampaigns.progress.workHint", defaultMessage: "Работа, которую ставит кампания", description: "Подсказка к бейджу с названием работы кампании" },
  window: { id: "canonCampaigns.progress.window", defaultMessage: "окно {from} — {to}", description: "Окно кампании в строке над показателями; {from}/{to} — даты; строчная" },
  windowFrozen: { id: "canonCampaigns.progress.windowFrozen", defaultMessage: "окно {from} — {to}, охват заморожен на {frozen}", description: "Окно кампании со снимком охвата; {frozen} — дата, на которую зафиксирован список полей кампании; строчная" },
  collapse: { id: "canonCampaigns.progress.collapse", defaultMessage: "Свернуть", description: "Кнопка в карточке прогресса: свернуть детальный прогресс кампании" },
  kpiFarms: { id: "canonCampaigns.progress.kpiFarms", defaultMessage: "Хозяйств отчиталось", description: "Подпись KPI: сколько хозяйств отчиталось из скольких" },
  kpiFarmsBottom: { id: "canonCampaigns.progress.kpiFarmsBottom", defaultMessage: "Хозяйство считается отчитавшимся, когда отчитались ВСЕ его контуры. Проверено: {verified} — отметка проверяющего, без своей доли.", description: "Нижняя строка KPI хозяйств; {verified} — число проверенных хозяйств" },
  kpiContours: { id: "canonCampaigns.progress.kpiContours", defaultMessage: "Контуров отчиталось", description: "Подпись KPI: сколько контуров отчиталось из скольких" },
  kpiContoursBottom: { id: "canonCampaigns.progress.kpiContoursBottom", defaultMessage: "Свёртка «всё или ничего»: частично закрытый контур не засчитывается целиком. Проверено: {verified}.", description: "Нижняя строка KPI контуров; {verified} — число проверенных контуров" },
  kpiWorks: { id: "canonCampaigns.progress.kpiWorks", defaultMessage: "Работ на полях отчиталось", description: "Подпись KPI: сколько работ на полях отчиталось из скольких" },
  kpiWorksBottom: { id: "canonCampaigns.progress.kpiWorksBottom", defaultMessage: "Минимальная единица работы — работа на поле (одна работа на одном контуре). Проверено: {verified}.", description: "Нижняя строка KPI работ на полях; {verified} — число проверенных" },
  reportedSummary: { id: "canonCampaigns.progress.reportedSummary", defaultMessage: "Отчитались {reported}, из них проверено {verified}. «Данных нет» ({noData}) сюда не входит: проверка ссылается на отчёт и без него не существует.", description: "Строка под KPI: числа работ на полях — отчитались, проверено, данных нет" },
  openQueue: { id: "canonCampaigns.progress.openQueue", defaultMessage: "Открыть очередь проверки", description: "Ссылка: перейти к очереди проверки этой кампании" },
  statesTitle: { id: "canonCampaigns.progress.statesTitle", defaultMessage: "Состояния работ на полях", description: "Заголовок полосы состава: распределение работ на полях по состояниям" },
  statesDenominator: { id: "canonCampaigns.progress.statesDenominator", defaultMessage: "работ на полях кампании", description: "Знаменатель полосы состава: что считаем (в родительном падеже, после числа)" },
  statesCaption: { id: "canonCampaigns.progress.statesCaption", defaultMessage: "«Данных нет» — это отсутствие отчёта, а не невыполнение; «конфликт» — отчёт и проверка расходятся, и обе записи остаются в базе.", description: "Подпись под полосой состояний работ на полях" },
  statesAbsent: { id: "canonCampaigns.progress.statesAbsent", defaultMessage: "Работ на полях в этой кампании нет: считать состав не от чего.", description: "Вместо полосы состава, когда в кампании нет работ на полях" },
  farmsCaption: { id: "canonCampaigns.farms.caption", defaultMessage: "Разбивка по хозяйствам — вход в детализацию по контурам. Сумма работ на полях по строкам равна итогу кампании: числа считаются из одних и тех же фактов. Сверху — те, кому раньше напоминать: просрочка и спор, затем незакрытая работа.", description: "Подпись (caption) таблицы хозяйств кампании: порядок и сверка с итогом" },
  farmCol: { id: "canonCampaigns.farms.colFarm", defaultMessage: "Хозяйство", description: "Заголовок колонки таблицы хозяйств кампании" },
  farmColState: { id: "canonCampaigns.farms.colState", defaultMessage: "Состояние", description: "Заголовок колонки таблицы хозяйств кампании: состояние работы хозяйства" },
  farmColDeadline: { id: "canonCampaigns.farms.colDeadline", defaultMessage: "Срок", description: "Заголовок колонки таблицы хозяйств кампании: срок / просрочка" },
  farmColWorks: { id: "canonCampaigns.farms.colWorks", defaultMessage: "Работ на полях", description: "Заголовок числовой колонки таблицы хозяйств кампании; узкая колонка" },
  farmColReported: { id: "canonCampaigns.farms.colReported", defaultMessage: "Отчитались", description: "Заголовок числовой колонки таблицы хозяйств кампании; узкая колонка" },
  farmColVerified: { id: "canonCampaigns.farms.colVerified", defaultMessage: "Проверено", description: "Заголовок числовой колонки таблицы хозяйств кампании; узкая колонка" },
  farmColContours: { id: "canonCampaigns.farms.colContours", defaultMessage: "Контуров", description: "Заголовок числовой колонки таблицы хозяйств кампании; узкая колонка" },
  farmFallback: { id: "canonCampaigns.farms.farmFallback", defaultMessage: "хозяйство #{id}", description: "Имя хозяйства в ссылке, когда названия нет; {id} — номер хозяйства" },
  linkContours: { id: "canonCampaigns.farms.linkContours", defaultMessage: "Контуры", description: "Ссылка в строке хозяйства: перейти к контурам этого хозяйства; коротко" },
  linkVerify: { id: "canonCampaigns.farms.linkVerify", defaultMessage: "Проверить", description: "Ссылка в строке хозяйства: перейти к проверке работы этого хозяйства; коротко" },
  farmsShown: {
    id: "canonCampaigns.farms.shown",
    defaultMessage: "Показано {shown} из {total, plural, one {# хозяйства} few {# хозяйств} many {# хозяйств} other {# хозяйства}}.",
    description: "Подпись под таблицей хозяйств кампании, когда список обрезан; {shown} — число показанных, # — общее число хозяйств в родительном падеже",
  },
  showAll: { id: "canonCampaigns.farms.showAll", defaultMessage: "Показать все", description: "Кнопка под таблицей хозяйств кампании: показать все строки" },
});

/**
 * Сколько хозяйств показывать сразу. В районе их 805, и вывалить все строки
 * в DOM значит сделать экран неотзывчивым ровно там, где человек ищет одно
 * хозяйство. Остальные не прячутся: их число названо, и раскрыть их можно
 * одной кнопкой.
 */
const FARMS_SHOWN = 50;

export function CampaignScreen() {
  const t = useT();
  const context = useAnalysisContext();
  const [search, setSearch] = useSearchParams();
  const chosen = search.get(CAMPAIGN_PARAM);

  const list = useRequest<CampaignListResponse>(
    () => api.campaigns({ ...context.params, limit: 50 }),
    [context.key],
  );

  const select = (id: number | null) => {
    const next = new URLSearchParams(search);
    if (id === null) next.delete(CAMPAIGN_PARAM);
    else next.set(CAMPAIGN_PARAM, String(id));
    setSearch(next, { replace: true });
  };

  return (
    <div className={features.stack}>
      <PageHeader
        title={t(m.title)}
        breadcrumb={
          <LevelTrail
            steps={[districtStep(context.linkSearch())]}
            screen={t(m.title)}
          />
        }
        purpose={t(m.purpose)}
      />

      <ScopeNotice scope={list.data?.context.scope} />

      {list.error && !list.data ? (
        <RequestErrorPanel
          error={list.error}
          title={t(m.loadFailed)}
          action={<Button onClick={list.reload}>{t(COMMON.retry)}</Button>}
        />
      ) : null}

      {list.data && list.data.items.length === 0 ? (
        <DataStatePanel state="empty" title={t(m.emptyTitle)}>
          {t(m.emptyText)}
        </DataStatePanel>
      ) : null}

      {list.data && list.data.items.length > 0 ? (
        <>
          <CampaignHeadline rows={list.data.items} />
          <OpenCampaigns rows={list.data.items} onSelect={select} />
          <CampaignWindows
            rows={list.data.items}
            asOf={
              list.data.context.as_of ?? list.data.context.snapshot_state_date
            }
          />
        </>
      ) : null}

      {list.data && list.data.items.length > 0 ? (
        <Disclosure
          title={t(m.allTitle)}
          summary={t(m.allSummary, { count: list.data.items.length })}
          more={t(m.allMore)}
        >
        <div className={features.scrollX}>
          <table className={features.plainTable}>
            <caption>
              {t(m.allCaption)}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t(m.colCampaign)}</th>
                <th scope="col">{t(m.colWork)}</th>
                <th scope="col">{t(m.colState)}</th>
                <th scope="col">{t(m.colWindow)}</th>
                <th scope="col" className={styles.splitColumn}>
                  {t(m.colProgress)}
                </th>
                <th scope="col" className={features.num}>
                  {t(m.colFarms)}
                </th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((row) => (
                <CampaignListRow
                  key={row.id}
                  row={row}
                  active={chosen === String(row.id)}
                  onSelect={() => select(row.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
        </Disclosure>
      ) : null}

      {chosen ? (
        <CampaignProgress id={Number(chosen)} onClose={() => select(null)} />
      ) : null}
    </div>
  );
}

/**
 * Кампания «идёт» — это СОСТОЯНИЕ, присланное сервером, а не вычисленное окно.
 *
 * Сравнивать даты окна с датой среза на клиенте здесь нельзя: горизонт
 * просрочки уже решает сервер (`worksModel.deadlineView`), и второе место,
 * решающее то же самое, однажды разошлось бы с первым молча.
 */
const RUNNING: string = "active";

interface CampaignTotals {
  /** Сумма обязательств по кампаниям среза. Складывать их можно: одно
   *  обязательство принадлежит ровно одной кампании, двойного счёта нет. */
  obligations: { total: number; reported: number; verified: number };
  /** Сколько кампаний сервер считает идущими. */
  running: number;
}

function campaignTotals(rows: ReadonlyArray<CampaignRow>): CampaignTotals {
  let total = 0;
  let reported = 0;
  let verified = 0;
  let running = 0;
  for (const row of rows) {
    total += row.obligations.total;
    reported += row.obligations.reported;
    verified += row.obligations.verified;
    if (row.status === RUNNING) running += 1;
  }
  return {
    obligations: { total, reported, verified },
    running,
  };
}

/**
 * Первый взгляд уровня района: не больше четырёх крупных величин.
 *
 * Хозяйства и гектары сюда НЕ складываются: одно хозяйство несёт работы
 * нескольких кампаний, и сумма по строкам посчитала бы его дважды. Ход
 * показан единственной разрешённой формой — двухосевой развилкой.
 */
function CampaignHeadline({ rows }: { rows: ReadonlyArray<CampaignRow> }) {
  const t = useT();
  const totals = useMemo(() => campaignTotals(rows), [rows]);

  return (
    <div className={`${layout.grid} ${layout.grid3}`}>
      <MetricCard
        label={t(m.headCount)}
        value={<Count value={rows.length} />}
        bottom={t(m.headCountBottom)}
      />
      <MetricCard
        label={t(m.headRunning)}
        value={<Count value={totals.running} />}
        bottom={t(m.headRunningBottom)}
        explain={t(m.headRunningExplain)}
      />
      <MetricCard
        label={t(m.headProgress)}
        explainLabel={t(m.headProgress)}
        value={
          <DoneSplit
            counts={totals.obligations}
            noun={m.nounWork}
            size="hero"
          />
        }
        bottom={t(m.headProgressBottom)}
        explain={t(m.headProgressExplain)}
      />
    </div>
  );
}

/**
 * Идущие кампании карточками — не больше бюджета первого взгляда.
 *
 * Таблица ниже отвечает на вопрос «что вообще есть»; эти карточки отвечают на
 * другой — «что прямо сейчас в поле». Остальные не спрятаны: их число названо,
 * и полная таблица раскрывается одним жестом.
 */
function OpenCampaigns({
  rows,
  onSelect,
}: {
  rows: ReadonlyArray<CampaignRow>;
  onSelect: (id: number) => void;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const running = rows.filter((row) => row.status === RUNNING);
  const head = preview(running, DENSITY.preview);

  if (running.length === 0) {
    return (
      <p className={styles.note}>{t(m.noRunning)}</p>
    );
  }

  return (
    <section className={features.stackTight}>
      <p className={styles.note}>
        {t(m.runningNote, {
          shown: head.shown.length,
          total: running.length,
          hasHidden: head.hidden > 0 ? "yes" : "no",
          hidden: head.hidden,
        })}
      </p>
      <div className={styles.openGrid}>
        {head.shown.map((row) => (
          <Card
            key={row.id}
            title={
              <>
                {row.title} <EvidenceBadge status={row.epistemic_status} />
              </>
            }
          >
            <p className={styles.note}>
              {t(m.cardNote, {
                work: row.work_name,
                from: formatDate(row.window_from),
                to: formatDate(row.window_to),
              })}
            </p>
            <DoneSplit counts={row.obligations} noun={m.nounWork} />
            <div className={features.rowWrap}>
              <Button onClick={() => onSelect(row.id)}>{t(m.progressBtn)}</Button>
              <Link
                to={{
                  pathname: "/verification",
                  search: context.linkSearch({ campaign_id: row.id }),
                }}
              >
                {t(m.verifyLink)}
              </Link>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

/**
 * Окна кампаний на одной оси времени.
 *
 * Таблица ниже отвечает на вопрос «что кампания собрала»; лента отвечает на
 * другой — «что и когда идёт в поле». Из таблицы это не читается: две даты в
 * ячейке не показывают ни того, что окна перекрываются, ни того, что одно уже
 * закончилось, а второе ещё не начиналось.
 *
 * Линия на ленте — ДАТА СРЕЗА с сервера, а не «сегодня». Вправо за последнее
 * известное окно лента не продолжается: прогноза здесь нет и не будет, пока в
 * корпусе нет временного ряда.
 */
function CampaignWindows({
  rows,
  asOf,
}: {
  rows: ReadonlyArray<CampaignRow>;
  asOf: string | null;
}) {
  const t = useT();
  // Порядок — по началу окна: лента читается слева направо, и порядок выдачи
  // на ней выглядел бы случайным.
  const dated = [...rows]
    .filter((row) => row.window_from !== null && row.window_to !== null)
    .sort((left, right) =>
      String(left.window_from).localeCompare(String(right.window_from)),
    );
  const undated = rows.length - dated.length;
  const head = preview(dated, DENSITY.tablePreview);

  if (dated.length === 0) {
    return null;
  }

  const timelineRows: TimelineRow[] = head.shown.map((row) => ({
    key: String(row.id),
    label: row.title,
    labelText: row.title,
    marks:
      row.epistemic_status === "synthetic_demo" ? (
        <Badge tone="demo">{t(m.demo)}</Badge>
      ) : null,
    bands: [
      {
        key: `window-${row.id}`,
        from: row.window_from as string,
        to: row.window_to as string,
      },
    ],
    note: t(m.windowsRowNote, {
      work: row.work_name,
      reported: formatCount(row.obligations.reported).text,
      total: formatCount(row.obligations.total).text,
    }),
  }));

  return (
    <Timeline
      title={t(m.windowsTitle)}
      denominator={t(m.windowsDenominator, { count: rows.length })}
      rows={timelineRows}
      asOf={asOf}
      asOfAbsent={t(m.windowsNoAsOf)}
      caption={[
        t(m.windowsCaption),
        head.hidden > 0 ? t(m.windowsHidden, { shown: head.shown.length, total: dated.length }) : "",
        undated > 0 ? t(m.windowsUndated, { count: undated }) : "",
      ]
        .filter(Boolean)
        .join(" ")}
    />
  );
}

function CampaignListRow({
  row,
  active,
  onSelect,
}: {
  row: CampaignRow;
  active: boolean;
  onSelect: () => void;
}) {
  const t = useT();
  return (
    <tr>
      <th scope="row">
        {row.title} <EvidenceBadge status={row.epistemic_status} />
      </th>
      <td>{row.work_name}</td>
      <td>
        {campaignStatusLabel(row.status).known ? (
          campaignStatusLabel(row.status).label
        ) : (
          <Badge tone="unknown" title={t(m.unknownState)}>
            {row.status}
          </Badge>
        )}
      </td>
      <td>
        {formatDate(row.window_from)} — {formatDate(row.window_to)}
      </td>
      {/* Три числовые колонки были тремя ответами на один вопрос, и знаменатель
          стоял отдельно от долей: «отчитались 40» рядом с «проверено 0»
          читалось как провал, а не как «проверяющий ещё не доехал». Развилка
          показывается единственным разрешённым способом — DoneSplit, с обеими
          осями и третьей величиной «без отчёта». */}
      <td className={styles.splitColumn}>
        <DoneSplit counts={row.obligations} noun={m.nounWork} />
      </td>
      <td className={features.num}>
        <Count value={row.actors.total} />
      </td>
      <td>
        <Button variant={active ? "primary" : "secondary"} onClick={onSelect}>
          {active ? t(m.shown) : t(m.progressBtn)}
        </Button>
      </td>
    </tr>
  );
}

function CampaignProgress({ id, onClose }: { id: number; onClose: () => void }) {
  const t = useT();
  const context = useAnalysisContext();
  const [showAll, setShowAll] = useState(false);
  const detail = useRequest<CampaignDetail>(
    () => api.campaignProgress(id, context.params),
    [context.key, id],
  );

  const campaign = detail.data?.campaign;
  const note = useMemo(
    () => (campaign ? windowNote(campaign) : null),
    [campaign],
  );

  // «Проверено» и «данных нет» — разные числа, и ни одно не выводится из
  // другого: проверка ссылается на отчёт и без него не существует.
  const stateCount = (key: string) =>
    campaign?.by_state.find((bucket) => bucket.key === key)?.count ?? 0;
  const noData = stateCount("no_data");

  // Порядок — по вниманию, а не по алфавиту: просрочка и спор наверху, потом
  // те, где работа не закончена. Алфавит здесь означал бы, что список читают
  // целиком, а его читают за ответом «кому напомнить».
  const farms = useMemo(() => {
    const rows = [...(campaign?.farms ?? [])];
    const rank = (row: (typeof rows)[number]) =>
      (row.overdue ? 0 : 2) +
      (row.state === "conflict" || row.state === "needs_clarification" ? 0 : 1);
    rows.sort(
      (left, right) =>
        rank(left) - rank(right) ||
        left.obligations.reported / Math.max(1, left.obligations.total) -
          right.obligations.reported / Math.max(1, right.obligations.total),
    );
    return rows;
  }, [campaign]);

  if (detail.error && !campaign) {
    return (
      <RequestErrorPanel
        error={detail.error}
        title={t(m.progressFailed)}
        action={<Button onClick={detail.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }
  if (!campaign) {
    return <DataStatePanel state="loading" title={t(m.progressLoading)} />;
  }

  return (
    <Card
      title={
        <>
          {campaign.title} <EvidenceBadge status={campaign.epistemic_status} />
        </>
      }
      large
    >
      <div className={features.rowWrap}>
        <Badge title={t(m.workHint)}>{campaign.work_name}</Badge>
        <Badge tone={campaign.window_elapsed === false ? "attention" : "neutral"}>
          {note?.label}
        </Badge>
        <span>
          {campaign.as_of_date
            ? t(m.windowFrozen, {
                from: formatDate(campaign.window_from),
                to: formatDate(campaign.window_to),
                frozen: formatDate(campaign.as_of_date),
              })
            : t(m.window, {
                from: formatDate(campaign.window_from),
                to: formatDate(campaign.window_to),
              })}
        </span>
        <Button onClick={onClose}>{t(m.collapse)}</Button>
      </div>

      {note?.hint ? <InlineAlert title={note.label}>{note.hint}</InlineAlert> : null}

      <div className={`${layout.grid} ${layout.gridFlow}`}>
        <MetricCard
          label={t(m.kpiFarms)}
          value={`${campaign.actors.reported} / ${campaign.actors.total}`}
          bottom={t(m.kpiFarmsBottom, { verified: campaign.actors.verified })}
        />
        <MetricCard
          label={t(m.kpiContours)}
          value={`${campaign.contours.reported} / ${campaign.contours.total}`}
          bottom={t(m.kpiContoursBottom, { verified: campaign.contours.verified })}
        />
        <MetricCard
          label={t(m.kpiWorks)}
          value={`${campaign.obligations.reported} / ${campaign.obligations.total}`}
          bottom={t(m.kpiWorksBottom, { verified: campaign.obligations.verified })}
        />
      </div>

      <p className={features.rowWrap}>
        <span>
          {t(m.reportedSummary, {
            reported: campaign.obligations.reported,
            verified: campaign.obligations.verified,
            noData,
          })}
        </span>
        <Link
          to={{
            pathname: "/verification",
            search: context.linkSearch({ campaign_id: campaign.id }),
          }}
        >
          {t(m.openQueue)}
        </Link>
      </p>

      <div className={progressStyles.progressGrid}>
        {denominatorsOf(campaign).map((view) => (
          <ProgressRow key={view.label} view={view} />
        ))}
        <AreaProgress area={campaign.area} />
      </div>
      <AxisLegend />

      {campaign.by_state.length > 0 ? (
        // Состав выборки — та же форма, что и на карантине: 100%-полоса со
        // знаменателем и легендой. Двухколоночная таблица знаменатель не
        // называла, и «данных нет» читалось как строка внизу списка, а не как
        // половина кампании.
        <StackedShare
          title={t(m.statesTitle)}
          segments={campaign.by_state.map((bucket) => ({
            key: bucket.key,
            label: obligationStateLabel(bucket.key).label,
            count: bucket.count,
          }))}
          total={campaign.obligations.total}
          denominator={t(m.statesDenominator)}
          noun={m.nounWork}
          epistemic={campaign.epistemic_status}
          caption={t(m.statesCaption)}
          absent={t(m.statesAbsent)}
        />
      ) : null}

      <div className={features.scrollX}>
        <table className={features.plainTable}>
          <caption>
            {t(m.farmsCaption)}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t(m.farmCol)}</th>
              <th scope="col">{t(m.farmColState)}</th>
              <th scope="col">{t(m.farmColDeadline)}</th>
              <th scope="col" className={features.num}>
                {t(m.farmColWorks)}
              </th>
              <th scope="col" className={features.num}>
                {t(m.farmColReported)}
              </th>
              <th scope="col" className={features.num}>
                {t(m.farmColVerified)}
              </th>
              <th scope="col" className={features.num}>
                {t(m.farmColContours)}
              </th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {(showAll ? farms : farms.slice(0, FARMS_SHOWN)).map((farm) => {
              const state = stateView(farm.state);
              const deadline = deadlineView({
                overdue: farm.overdue,
                window_to: campaign.window_to,
                window_elapsed: campaign.window_elapsed,
              });
              return (
                <tr key={farm.farm_obligation_id || farm.actor.id}>
                  <th scope="row">
                    <Link
                      to={{
                        pathname: `/actors/${farm.actor.id}`,
                        search: context.linkSearch(),
                      }}
                    >
                      {farm.actor.name ?? t(m.farmFallback, { id: farm.actor.id })}
                    </Link>
                  </th>
                  <td>
                    <Badge tone={state.attention ? "attention" : "neutral"}>
                      {state.label}
                    </Badge>
                  </td>
                  <td>
                    <Badge tone={deadline.overdue ? "attention" : "neutral"}>
                      {deadline.label}
                    </Badge>
                  </td>
                  <td className={features.num}>
                    <Count value={farm.obligations.total} />
                  </td>
                  <td className={features.num}>
                    <Count value={farm.obligations.reported} />
                  </td>
                  <td className={features.num}>
                    <Count value={farm.obligations.verified} />
                  </td>
                  <td className={features.num}>
                    <Count value={farm.contours.total} />
                  </td>
                  <td>
                    {farm.farm_obligation_id ? (
                      <span className={features.rowWrap}>
                        <Link
                          to={{
                            pathname: "/my-works",
                            search: context.linkSearch({
                              actor_id: farm.actor.id,
                            }),
                          }}
                        >
                          {t(m.linkContours)}
                        </Link>
                        {/* Пятого уровня своим адресом пока нет: `/works/:id`
                            не заведён. Поэтому ссылка ведёт в очередь проверки
                            с тем же срезом и якорем на строку этой работы — то
                            есть в то место, где по ней действительно
                            высказываются. */}
                        <Link
                          to={{
                            pathname: "/verification",
                            search: context.linkSearch({
                              campaign_id: campaign.id,
                              actor_id: farm.actor.id,
                            }),
                            hash: `#${queueAnchor(farm.farm_obligation_id)}`,
                          }}
                        >
                          {t(m.linkVerify)}
                        </Link>
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {farms.length > FARMS_SHOWN ? (
          <p>
            {t(m.farmsShown, { shown: showAll ? farms.length : FARMS_SHOWN, total: farms.length })}{" "}
            {showAll ? null : (
              <Button onClick={() => setShowAll(true)}>{t(m.showAll)}</Button>
            )}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
